import { useMemo } from 'react'

import { CollectionType } from '@jotta/grpc-connect-openapi/collection'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { DropdownMenuItem } from '@jotta/ui/DropdownMenu'
import { cn } from '@jotta/utils/css'

import type { CollectionRef } from '../../api/useCollection'
import { useAlbumId, useCollection } from '../../api/useCollection'
import { useSelectionStore } from '../../store/PhotoContext'
import type { SelectablePhoto } from '../../store/SelectionStore'
import type { ActionDispatcherProps } from '../PhotoActions/PhotoActions'
import { PhotoAction } from '../PhotoActions/PhotoActions'
import { useAddPhotoMode } from '../PhotoGallery/useAddPhotoMode'

import { MediaThumbActionsMenu } from './MediaThumbActionsMenu'

function MediaThumbActionDispatcher({
  label,
  icon,
  testid,
  loading,
  onClick,
}: ActionDispatcherProps) {
  const isDisabled = loading

  return (
    <DropdownMenuItem
      className={cn(
        'group flex-row gap-4 rounded-lg p-2',
        isDisabled && 'pointer-events-none',
      )}
      disabled={isDisabled}
      onSelect={() => {
        if (isDisabled) {
          return
        }
        onClick()
      }}
      data-testid={testid}
    >
      {icon && (
        <PlainBrandIcon
          icon={icon}
          width={20}
          height={20}
          className="group-data-[highlighted]:text-on-primary-container"
        />
      )}
      <span className="text-sm font-medium group-data-[highlighted]:text-on-primary-container">
        {label}
      </span>
    </DropdownMenuItem>
  )
}

export function MediaThumbActionsMenuButton({
  photo,
  collectionRef,
}: {
  photo: SelectablePhoto
  collectionRef: CollectionRef
}) {
  const selection = useSelectionStore()
  const selectedPhotos = selection.values
  const photoId = photo.id
  const albumId = useAlbumId(collectionRef)
  const { data } = useCollection(collectionRef)
  const collection = data?.case === 'collection' ? data.value : undefined
  const [addPhotoMode] = useAddPhotoMode()

  const photos = useMemo(
    () => (selection.has(photoId) ? selectedPhotos : [photo]),
    [photo, photoId, selectedPhotos, selection],
  )

  const isLocationAlbum = collection?.type === CollectionType.LOCATION_ALBUM
  const isSelected = selection.has(photoId)
  const disabled = Boolean(albumId && selection.hasSelection && !isSelected)

  // In add-photos mode PhotoAction.Provider renders the dispatcher directly
  // instead of the dropdown, which would put a MenuItem outside a Menu
  if (isLocationAlbum || addPhotoMode) {
    return null
  }

  return (
    <PhotoAction.Provider
      collectionRef={collectionRef}
      photos={photos}
      actionDispatcherComponent={MediaThumbActionDispatcher}
    >
      <MediaThumbActionsMenu albumId={albumId} disabled={disabled} />
    </PhotoAction.Provider>
  )
}
